<template>
  <div class="reset-password">
    
<FormKitLazyProvider>
<slot name="content">
      <h1 class="my-account__title heading heading--1">
        {{ $t('Set a new password') }}
      </h1>

      <div class="my-account__text">
        <p>
          {{ $t('Enter your new password below to regain access to your account.') }}
        </p>
      </div>
    </slot>

    <FormKit
      v-show="!success"
      type="form"
      :incomplete-message="$t('Sorry, not all fields are filled out correctly.')"
      :value="data"
      :actions="false"
      @submit="onSubmit"
    >
      <slot name="newPassword">
        <FormKit
          :type="showPassword ? 'text' : 'password'"
          :label="$t('New password') + ' *'"
          name="newPassword"
          validation="required"
          :validation-label="$t('New password')"
        >
          <template #suffix>
            <button
              type="button"
              class="formkit-password-toggle"
              @click.prevent="showPassword = !showPassword"
            >
              <IconEyeOpen
                v-if="!showPassword"
                class="formkit-password-toggle__icon icon icon--eye-open"
              />
              <IconEyeClosed
                v-if="showPassword"
                class="formkit-password-toggle__icon icon icon--eye-closed"
              />
              <span class="formkit-password-toggle__label">{{ showPassword ? $t('Hide password') : $t('Show password') }}</span>
            </button>
          </template>
        </FormKit>
      </slot>
      <slot name="confirmPassword">
        <FormKit
          :type="showPassword2 ? 'text' : 'password'"
          :label="$t('Confirm new password') + ' *'"
          name="confirmPassword"
          validation="required|confirm:newPassword"
          :validation-label="$t('Confirm new password')"
        >
          <template #suffix>
            <button
              type="button"
              class="formkit-password-toggle"
              @click.prevent="showPassword2 = !showPassword2"
            >
              <IconEyeOpen
                v-if="!showPassword2"
                class="formkit-password-toggle__icon icon icon--eye-open"
              />
              <IconEyeClosed
                v-if="showPassword2"
                class="formkit-password-toggle__icon icon icon--eye-closed"
              />
              <span class="formkit-password-toggle__label">{{ showPassword2 ? $t('Hide password') : $t('Show password') }}</span>
            </button>
          </template>
        </FormKit>
      </slot>

      <div class="formkit-buttons">
        <slot
          name="actions"
          v-bind="{ resetPasswordLoading }"
        >
          <button
            :disabled="resetPasswordLoading"
            type="submit"
            class="button button--cta"
            :class="{ 'button--loading': resetPasswordLoading }"
          >
            <span>{{ $t('Save') }}</span>
            <IconLoader
              v-if="resetPasswordLoading"
              class="icon icon--loader"
            />
            <IconArrow class="icon icon--right icon--arrow icon--arrow-right" />
          </button>
        </slot>
      </div>
    </FormKit>

    <slot
      v-if="success"
      name="success"
      :login-loading="loginLoading"
      :login-error="loginError"
    >
      <p>
        Successfully reset password! <span v-if="loginLoading"> - logging in ...</span>
      </p>
    </slot>
</FormKitLazyProvider>

  </div>
</template>

<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
const route = useRoute()
export interface Props {
  autoLogin?: boolean
  redirect?: any
}
const props = defineProps<Props>()

const { resetPassword, resetPasswordError, resetPasswordLoading } = useAccountResetPassword()
const { login, loginLoading, loginError } = useAccountLogin()
const data = ref({
  email: '',
  newPassword: '',
})

const showPassword = ref<boolean>(false)
const showPassword2 = ref<boolean>(false)

const success = ref<boolean>(false)

const onSubmit = async (formData: Omit<Parameters<typeof resetPassword>[0], 'email' | 'resetPasswordToken'> & { confirmPassword: string }) => {
  const { confirmPassword: _, ...input } = formData
  await resetPassword({
    ...input,
    email: route.query.email || '',
    resetPasswordToken: route.query.token || '',
  })
  if (!resetPasswordError.value) {
    success.value = true
    if (props.autoLogin) {
      await login({ email: route.query.email || '', password: formData.newPassword })
    }
    if (props.redirect) {
      navigateTo(props.redirect)
    }
  }
}
</script>

<style>
@import url("~layers/app/components/Account/ResetPassword.css");
</style>
